import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { i18n } from '@/i18n/i18n';
import { useNotificationStore } from '@/stores/notification.store';
import { useSessionStore } from '@/stores/session.store';
import router from '@/router';
import logoutService from '@/services/auth/logout.service';
import {
  startRevokeRedirectFlow,
  stopRevokeRedirectFlow
} from '@/services/api/client/revocation-redirect-state';

/**
 * Deduplication guard: if a session revalidation is already in-flight due to a
 * prior 403, skip triggering another one. Prevents multiple concurrent 403
 * responses (e.g., parallel API calls on a page) from each firing a re-fetch.
 */
let pendingSessionRevalidation: Promise<void> | null = null;

export function handleErrorResponse(error: unknown) {
  const sessionStore = useSessionStore();

  // During logout, in-flight requests may receive 401/403 because the token is
  // being revoked. Suppress all error handling to prevent spurious "forbidden"
  // notifications appearing on the login page.
  if (sessionStore.isLoggingOut) {
    return;
  }

  // Silently ignore cancelled requests (component-level AbortController cleanup).
  if (axios.isCancel(error)) {
    return;
  }

  if (axios.isAxiosError(error) && error.response) {
    if (error.config?.params?.ignoreError) {
      return;
    }

    const { status } = error.response;

    // Server Errors
    if (status >= 500) {
      // showErrorNotificationFromResponse(error);
    }

    // Unauthorised
    if (status === 401) {
      logoutService(false);
      if (error.response.data.error === 'password_reset_required') {
        throw error;
      }
    }

    // Forbidden
    if (status === 403) {
      Sentry.captureException(error);

      const current = router.currentRoute.value;
      const routePermission = current?.meta?.permission as string | undefined;
      const routeOrgId = current?.params?.organisationId as string | undefined;
      const isProtectedRoute =
        (current?.meta?.isAdmin && !current?.meta?.feature) ||
        current?.meta?.isLensSupport;
      const isOrgPermissionRoute = !!routeOrgId && !!routePermission;

      // Avoid duplicate notifications on domain-admin feature-gated routes.
      // The response interceptor in interceptors.ts handles redirect for those.
      const isAdminFeatureRoute = current?.meta?.isAdmin && current?.meta?.feature;
      const shouldShowForbiddenSnackbar = !isAdminFeatureRoute && !isProtectedRoute && !isOrgPermissionRoute;

      // If a session revalidation is already in-flight (from a concurrent 403),
      // skip triggering another — the first will handle the redirect.
      if (pendingSessionRevalidation) {
        return;
      }

      if (shouldShowForbiddenSnackbar) {
        useNotificationStore().snackbar = {
          label: i18n.global.t('platform.error.403'),
          autoDismiss: true,
          dismissAfter: 6000,
          color: 'danger',
          icon: 'warning'
        };
      }

      const hasOrgContext = !!current?.params?.organisationId;

      if (isProtectedRoute) {
        // Re-fetch session data to get the accurate role/permission state, then
        // redirect based on what the user actually has access to — not stale
        // cached values. The session store update also cascades reactively to
        // the v-allow directive and permission-based getters.
        startRevokeRedirectFlow();
        pendingSessionRevalidation = (async () => {
          try {
            await sessionStore.fetchCurrentUser(true);
            await sessionStore.fetchOrganisations();
            const org = sessionStore.customerOrganisations?.[0] ?? sessionStore.organisations?.[0];
            if (org) {
              router.replace({ name: 'patient-list', params: { organisationId: org.id } });
            } else {
              router.replace({ name: 'settings' });
            }
          } finally {
            stopRevokeRedirectFlow();
            pendingSessionRevalidation = null;
          }
        })();
      } else if (hasOrgContext && !isAdminFeatureRoute) {
        // For org-scoped 403s: re-fetch organisations, then redirect if the
        // current route org is no longer accessible. We keep the
        // OrganisationLayout watcher as a secondary safety net.
        startRevokeRedirectFlow();
        pendingSessionRevalidation = (async () => {
          try {
            await sessionStore.fetchOrganisations();

            const routeOrgId = current?.params?.organisationId as string | undefined;
            const hasRouteOrgAccess = !!routeOrgId && !!sessionStore.organisations?.some((org) => org.id === routeOrgId);

            if (!hasRouteOrgAccess) {
              const org = sessionStore.customerOrganisations?.[0] ?? sessionStore.organisations?.[0];
              if (org) {
                router.replace({ name: 'patient-list', params: { organisationId: org.id } });
              } else {
                router.replace({ name: 'settings' });
              }
              return;
            }

            // Organisation access still exists, but route-level permission may
            // have been revoked in another session (e.g. org admin role
            // removed). Refresh the org payload (includes permissions) and
            // redirect silently when current route permission is no longer
            // granted.
            if (routeOrgId && routePermission) {
              try {
                await sessionStore.fetchOrganisation(routeOrgId);
              } catch (e) {
                // Ignore refresh errors here; fallback stays on current route.
              }

              const refreshedOrg = sessionStore.getOrganisation(routeOrgId);
              const permissionKeys = refreshedOrg?.permissions?.map((permission) => permission.key) ?? [];
              const hasRoutePermission = permissionKeys.includes(routePermission);

              if (!hasRoutePermission) {
                if (permissionKeys.includes('patient:read')) {
                  router.replace({ name: 'patient-list', params: { organisationId: routeOrgId } });
                } else if (permissionKeys.includes('anonymous-participant:read')) {
                  router.replace({ name: 'participant-list', params: { organisationId: routeOrgId } });
                } else {
                  router.replace({ name: 'organisation-user-profile', params: { organisationId: routeOrgId } });
                }
              }
            }
          } finally {
            stopRevokeRedirectFlow();
            pendingSessionRevalidation = null;
          }
        })();
      }
    }

    // Maintenance
    if (status === 503) {
      sessionStore.isMaintenance = true;
    }

    // Conflict
    if (status === 409) {
      // showErrorNotificationFromResponse(error);
    }
  } else {
    Sentry.captureException(error);
  }
}
